import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Tab, TabPanel, Tabs } from '@dynamon-as/ui';
import { Drafts } from '@mui/icons-material';
import type { Request } from '@features/requests/types';
import { REQUEST_TABS } from '@features/requests/constants';
import type { RequestTabValue } from '@features/requests/constants';
import {
    sortByEndDesc,
    sortByStartAsc,
    sortByUpdatedDesc,
} from '@features/requests/utils';
import { useRequestTabs } from '@features/requests/hooks';
import { RequestListItem } from './components/RequestListItem';
import { useI18n, useRequestTranslations } from '@shared/i18n';

import styles from './RequestList.module.css';

/**
 * Props for RequestList component
 */
export interface RequestListProps {
    /** List of requests to display */
    requests: Request[];
    /** Callback when a request is selected */
    onSelect: (id: string) => void;
    /** Callback when user wants to edit a request */
    onEdit?: (id: string) => void;
    /** Callback when user wants to submit a request */
    onSubmit?: (id: string) => void;
    /** Optional active date for Current/Past separation */
    activeDate?: Date;
}

/**
 * Displays a list of requests grouped in Drafts / Current / Past
 * tabs with per-tab counters, sorting and empty states.
 */
export const RequestList = React.memo(function RequestList({
    requests,
    onSelect,
    onEdit,
    onSubmit,
    activeDate,
}: RequestListProps): React.ReactElement {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { getTabLabel } = useRequestTranslations();
    const [activeTab, setActiveTab] = useState<RequestTabValue>('drafts');

    const handleEdit = useCallback(
        (requestId: string): void => {
            if (onEdit) {
                onEdit(requestId);
            } else {
                navigate(`/requests/${requestId}/edit`);
            }
        },
        [navigate, onEdit],
    );

    const handleSubmit = useCallback(
        (requestId: string): void => {
            if (onSubmit) {
                onSubmit(requestId);
            } else {
                navigate(`/requests/${requestId}`);
            }
        },
        [navigate, onSubmit],
    );

    const { draftRequests, currentRequests, pastRequests } = useRequestTabs({
        requests,
        activeDate,
    });

    // Sorted requests per tab
    const tabRequestsMap = useMemo(
        (): Record<RequestTabValue, Request[]> => ({
            drafts: sortByUpdatedDesc(draftRequests),
            current: sortByStartAsc(currentRequests),
            past: sortByEndDesc(pastRequests),
        }),
        [draftRequests, currentRequests, pastRequests],
    );

    // Per-tab counters shown in the tab labels
    const tabCountMap = useMemo(
        (): Record<RequestTabValue, number> => ({
            drafts: draftRequests.length,
            current: currentRequests.length,
            past: pastRequests.length,
        }),
        [draftRequests.length, currentRequests.length, pastRequests.length],
    );

    const renderEmptyState = (tabValue: RequestTabValue): React.ReactNode => {
        const tabConfig = REQUEST_TABS.find(
            (tab): boolean => tab.value === tabValue,
        );
        const EmptyIcon = tabConfig?.Icon ?? Drafts;

        return (
            <div className={styles.emptyTab}>
                <EmptyIcon className={styles.emptyIcon} />
                <p className={styles.emptyMessage}>
                    {t(`requests.list.empty.${tabValue}`)}
                </p>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {/* Tabs and Panels Container */}
            <div className={styles.tabsAndPanels}>
                <div className={styles.tabsContainer}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, value): void =>
                            setActiveTab(value as RequestTabValue)
                        }
                        className={styles.tabs}
                    >
                        {REQUEST_TABS.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={`${getTabLabel(tab.value as 'drafts' | 'current' | 'past')} (${tabCountMap[tab.value]})`}
                            />
                        ))}
                    </Tabs>
                </div>

                {REQUEST_TABS.map((tab): React.ReactElement => {
                    const tabRequests = tabRequestsMap[tab.value];

                    return (
                        <TabPanel
                            key={tab.value}
                            index={tab.value}
                            activeIndex={activeTab}
                        >
                            <div className={styles.tabContent}>
                                {tabRequests.length > 0 ? (
                                    <ul className={styles.list}>
                                        {tabRequests.map(
                                            (request): React.ReactElement => (
                                                <RequestListItem
                                                    key={request.id}
                                                    request={request}
                                                    onSelect={onSelect}
                                                    onEdit={handleEdit}
                                                    onSubmit={handleSubmit}
                                                />
                                            ),
                                        )}
                                    </ul>
                                ) : (
                                    renderEmptyState(tab.value)
                                )}
                            </div>
                        </TabPanel>
                    );
                })}
            </div>
        </div>
    );
});
